import React from 'react'
import { CheckboxOption, RadioOption, SelectOption } from '../type'
import { FileWrapper } from './FormFileField'
import { FieldValues, UseFormReturn } from 'react-hook-form'
import { ControllerRenderProps } from 'react-hook-form'
import { OffsetPaginationRO } from '@/type'

export type FormFieldProps<F extends FormFieldData> = {
  data: F
  field: ControllerRenderProps<FieldValues, F['key']>
}

export enum FormFieldType {
  NUMBER,
  STRING,
  TEXT,
  DATE,
  SELECT,
  CHECKBOXGROUP,
  CHECKBOX,
  FILE,
  CUSTOM_READ_ONLY,
  EMBED,
  COLOR,
  KEYWORDS,
  RADIOGROUP,
  CKEDITOR,
  FIELD_GROUP_READ_ONLY,
  RELATION,
  REORDER,
  JSON,
  SWITCH,
  HASHTAG,
}

export type FormFieldCommon<T = any, R = any> = {
  label: string
  key: keyof T | (string & { void?: never })
  readOnly?: boolean
  resolver?: (data: T) => R | undefined
  default?: R
  required?: boolean
}

export type FormDateType<T = any> = FormFieldCommon<T, Date | string> & {
  type: FormFieldType.DATE
  timePicker?: boolean
  defaultHours?: number
  defaultMinutes?: number
  default?: Date | string
}
export type FormNumberType<T = any> = FormFieldCommon<T, number> & {
  type: FormFieldType.NUMBER
  default?: number
  placeholder?: number | string
}
export type FormStringType<T = any> = FormFieldCommon<T, string> & {
  type: FormFieldType.STRING
  default?: string | (() => string)
  placeholder?: number | string
  secure?: boolean
}
export type FormTextType<T = any> = FormFieldCommon<T, string> & {
  type: FormFieldType.TEXT
  height?: number
  default?: string
  placeholder?: string
}
export type FormSelectType<T = any> = FormFieldCommon<T, string | number> & {
  type: FormFieldType.SELECT
  options: SelectOption[]
  default?: string | number | (() => string | number | undefined)
  placeholder?: string
}
export type FormCheckboxGroupType<T = any> = FormFieldCommon<T> & {
  type: FormFieldType.CHECKBOXGROUP
  options: CheckboxOption[]
  default?: any
}
export type FormCheckboxType<T = any> = FormFieldCommon<T, boolean> & {
  type: FormFieldType.CHECKBOX
  default?: boolean
}
export type FormFileType<T = any> = FormFieldCommon<T> & {
  type: FormFieldType.FILE
  multiple?: boolean
  accept?: string
  default?: any
  maxCount?: number
  crop?:
    | {
        autoOpen?: boolean
        aspectRatio?: number
      }
    | boolean
  uploadHandler?: (file: FileWrapper) => Promise<any>
  imageAlertText?: string
}
export type FormCustomReadOnlyType<T = any> = FormFieldCommon<
  T,
  React.ReactNode
> & {
  type: FormFieldType.CUSTOM_READ_ONLY
  isHidden?: (data: T) => any | Promise<any>
  render: (data?: T, context?: UseFormReturn<FieldValues>) => React.ReactNode
}
export type FormEmbedType<T = any> = FormFieldCommon<T> & {
  type: FormFieldType.EMBED
  src: string
  className?: string
  on?: (
    message: any,
    iframe: HTMLIFrameElement,
    state: T,
  ) => any | Promise<any> | undefined
}
export type FormColorType<T = any> = FormFieldCommon<T, string> & {
  type: FormFieldType.COLOR
  default?: string | (() => string)
}
export type FormKeywordsType<T = any> = FormFieldCommon<T, string[]> & {
  type: FormFieldType.KEYWORDS
  searchApiFn: (args: {
    searchText: string
    amount: number
    page: number
  }) => Promise<OffsetPaginationRO<any>>
}
export type FormRadioGroupType<T = any> = FormFieldCommon<T> & {
  type: FormFieldType.RADIOGROUP
  options: RadioOption[]
  default?: any
}
export type FormCKEditorType<T = any> = FormFieldCommon<T> & {
  type: FormFieldType.CKEDITOR
  default?: any
  imageAlertText?: string
}
export type FormSwitchType<T = any> = FormFieldCommon<T, boolean> & {
  type: FormFieldType.SWITCH
  default?: boolean
}

export type FormHashtagType<T = any> = FormFieldCommon<T, string[]> & {
  type: FormFieldType.HASHTAG
  default?: string[]
  placeholder?: string
  resolver?: (data: T) => string[] | undefined
}

export type FormFieldGroupReadOnlyType<T = any> = FormFieldCommon<T> & {
  type: FormFieldType.FIELD_GROUP_READ_ONLY
  fields: FormFieldData<T>[]
  fieldDirection?: 'row' | 'column'
}

export type FormRelationType<T = any> = FormFieldCommon<T> & {
  type: FormFieldType.RELATION
  multiple?: boolean
  searchApiFn?: (args: {
    searchText: string
    amount: number
    page: number
  }) => Promise<OffsetPaginationRO<any>>
  labelKey?: string
  href?: (data: T) => string | undefined
  onAdd?: (data: T) => void
  onRemove?: (data: T) => void
}

export type FormReorderType<T = any> = FormFieldCommon<T> & {
  type: FormFieldType.REORDER
}

export type FormJsonType<T = any> = FormFieldCommon<T> & {
  type: FormFieldType.JSON
}

export type FormFieldData<T = any> =
  | FormDateType<T>
  | FormNumberType<T>
  | FormStringType<T>
  | FormTextType<T>
  | FormSelectType<T>
  | FormCheckboxGroupType<T>
  | FormCheckboxType<T>
  | FormFileType<T>
  | FormCustomReadOnlyType<T>
  | FormEmbedType<T>
  | FormColorType<T>
  | FormKeywordsType<T>
  | FormRadioGroupType<T>
  | FormCKEditorType<T>
  | FormFieldGroupReadOnlyType<T>
  | FormRelationType<T>
  | FormReorderType<T>
  | FormJsonType<T>
  | FormSwitchType<T>
  | FormHashtagType<T>
